import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../../../../utils/api';

interface BriefingQuoteProps {
  className?: string;
}

interface BriefingData {
  briefing: string;
  recommended_signal_id?: string;
  customer_name?: string;
  generated_at: string;
  error?: string;
}

export const BriefingQuote: React.FC<BriefingQuoteProps> = ({ className = '' }) => {
  const [briefing, setBriefing] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Don't fetch if we already have a briefing or there was an error
    if (briefing || error) return;
    
    // Tracking variable to prevent state updates after unmount
    let isMounted = true;
    
    const fetchBriefing = async () => {
      try {
        // Use the correct API path with the v1 prefix that matches our route definition
        const response = await fetchWithAuth('/v1/briefing/daily');
        // Only update state if component is still mounted
        if (isMounted) {
          console.log('Briefing response:', response);
          setBriefing(response);
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMounted) {
          console.error('Failed to fetch briefing:', err);
          setError('Could not load today\'s briefing');
        }
      } finally {
        // Only update state if component is still mounted
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBriefing();
    
    // Cleanup function to prevent memory leaks and state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSignalClick = () => {
    if (briefing?.recommended_signal_id) {
      navigate(`/v3/vault/${briefing.recommended_signal_id}`);
    }
  };

  // Display skeleton placeholder while loading
  if (loading) {
    return (
      <div 
        className={`relative mb-6 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-400 overflow-hidden ${className}`}
      >
        <div className="flex items-center p-4 sm:p-5">
          {/* Avatar/Icon Skeleton */}
          <div className="shrink-0 rounded-full bg-white/20 p-2 mr-4">
            <div className="h-5 w-5 bg-white/40 rounded-full animate-pulse"></div>
          </div>
          
          {/* Content Skeleton */}
          <div className="flex-1">
            <div className="h-4 bg-white/40 rounded-full w-3/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-white/40 rounded-full w-full mb-2 animate-pulse"></div>
            <div className="h-4 bg-white/40 rounded-full w-2/3 animate-pulse"></div>
          </div>
          
          {/* Attribution Skeleton */}
          <div className="shrink-0 ml-4 text-right">
            <div className="h-4 bg-white/40 rounded-full w-12 mb-1 animate-pulse"></div>
            <div className="h-3 bg-white/40 rounded-full w-24 animate-pulse"></div>
          </div>
        </div>
        
        {/* Decorative circle overlay */}
        <div className="absolute -top-10 right-10 w-32 h-32 rounded-full bg-white/10"></div>
      </div>
    );
  }

  // Show a static placeholder with error indication when there's an error
  if (error && !briefing) {
    return (
      <div 
        className={`relative mb-6 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-400 overflow-hidden ${className}`}
      >
        <div className="flex items-center p-4 sm:p-5">
          {/* Avatar/Icon */}
          <div className="shrink-0 rounded-full bg-white/20 p-2 mr-4">
            <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
              <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" fill="currentColor"/>
            </svg>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <p className="text-sm sm:text-base font-normal text-white">
              Intelligence briefing is currently unavailable. Please try again later.
            </p>
          </div>
          
          {/* Attribution */}
          <div className="shrink-0 ml-4 text-right">
            <div className="text-sm font-bold text-white">Juno</div>
            <div className="text-xs text-white/80">
              Today's Intelligence
            </div>
          </div>
        </div>
        
        {/* Decorative circle overlay */}
        <div className="absolute -top-10 right-10 w-32 h-32 rounded-full bg-white/10"></div>
      </div>
    );
  }

  // Show a placeholder for empty briefing
  if (!briefing?.briefing) {
    return (
      <div 
        className={`relative mb-6 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-400 overflow-hidden ${className}`}
      >
        <div className="flex items-center p-4 sm:p-5">
          {/* Avatar/Icon */}
          <div className="shrink-0 rounded-full bg-white/20 p-2 mr-4">
            <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 7.5C13.66 7.5 15 8.84 15 10.5C15 12.16 13.66 13.5 12 13.5C10.34 13.5 9 12.16 9 10.5C9 8.84 10.34 7.5 12 7.5ZM12 20C9.5 20 7.29 18.82 6 16.95C6.03 14.98 10 13.9 12 13.9C13.99 13.9 17.97 14.98 18 16.95C16.71 18.82 14.5 20 12 20Z" fill="currentColor"/>
            </svg>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <p className="text-sm sm:text-base font-normal text-white">
              No intelligence briefing available today. Check back later for updates.
            </p>
          </div>
          
          {/* Attribution */}
          <div className="shrink-0 ml-4 text-right">
            <div className="text-sm font-bold text-white">Juno</div>
            <div className="text-xs text-white/80">
              Today's Intelligence
            </div>
          </div>
        </div>
        
        {/* Decorative circle overlay */}
        <div className="absolute -top-10 right-10 w-32 h-32 rounded-full bg-white/10"></div>
      </div>
    );
  }

  return (
    <div 
      className={`relative mb-6 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-400 overflow-hidden ${className}`}
    >
      <div className="flex items-center p-4 sm:p-5">
        {/* Avatar/Icon */}
        <div className="shrink-0 rounded-full bg-white/20 p-2 mr-4">
          <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 7.5C13.66 7.5 15 8.84 15 10.5C15 12.16 13.66 13.5 12 13.5C10.34 13.5 9 12.16 9 10.5C9 8.84 10.34 7.5 12 7.5ZM12 20C9.5 20 7.29 18.82 6 16.95C6.03 14.98 10 13.9 12 13.9C13.99 13.9 17.97 14.98 18 16.95C16.71 18.82 14.5 20 12 20Z" fill="currentColor"/>
          </svg>
        </div>
        
        {/* Content */}
        <div className="flex-1">
          <blockquote className="text-sm sm:text-base font-normal text-white">
            {briefing?.briefing}
          </blockquote>
          
          {briefing?.recommended_signal_id && (
            <button 
              onClick={handleSignalClick}
              className="mt-2 text-xs sm:text-sm font-medium text-white/90 hover:text-white transition-colors flex items-center"
            >
              View signal
              <svg className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Attribution */}
        <div className="shrink-0 ml-4 text-right">
          <div className="text-sm font-bold text-white">Juno</div>
          <div className="text-xs text-white/80">
            Today's Intelligence
          </div>
        </div>
      </div>
      
      {/* Decorative circle overlay, similar to NO HARM DONE design */}
      <div className="absolute -top-10 right-10 w-32 h-32 rounded-full bg-white/10"></div>
    </div>
  );
};